import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IUpdateStaffRequest,
  IUpdateStaffUserRequest,
} from "../../../@types/admin/AdminStaff";
import { staffService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/** Invalidates everything the staff pages show for one staff member. */
function useStaffInvalidation() {
  const queryClient = useQueryClient();

  return (staffId: number, userId?: number) => {
    queryClient.invalidateQueries({ queryKey: ["admin-staff-list"] });
    queryClient.invalidateQueries({
      queryKey: ["admin-staff-details", staffId],
    });
    if (userId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ["admin-staff-user", userId] });
    }
  };
}

export function useUpdateStaff() {
  const invalidate = useStaffInvalidation();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (request: IUpdateStaffRequest) =>
      ensureOk(await staffService.updateStaff(request)),
    onSuccess: (_data, request) => invalidate(request.id),
  });

  return { updateStaff: mutateAsync, isUpdatingStaff: isPending };
}

export function useUpdateStaffUser() {
  const invalidate = useStaffInvalidation();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (request: IUpdateStaffUserRequest) =>
      ensureOk(await staffService.updateUser(request)),
    // The list shows the user's name and email, so it goes stale too.
    onSuccess: (_data, request) => invalidate(0, request.id),
  });

  return { updateStaffUser: mutateAsync, isUpdatingStaffUser: isPending };
}

/**
 * Re-enables a deactivated staff member. Retool signalled the host page with a
 * `reactivateTick` output so the list refetched; invalidating the queries does
 * the same job here — the account's `isActive` drives the button's own
 * disabled state, so the user record must be re-read as well.
 */
export function useReactivateStaff() {
  const invalidate = useStaffInvalidation();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ staffId }: { staffId: number; userId?: number }) =>
      ensureOk(await staffService.reactivateStaff(staffId)),
    onSuccess: (_data, { staffId, userId }) => invalidate(staffId, userId),
  });

  return { reactivateStaff: mutateAsync, isReactivatingStaff: isPending };
}
